import * as React from 'react';
import { cn } from '@antimatterhq/ui/lib/utils';
import Tooltip from '@antimatterhq/ui/components/tooltip';
import { Skeleton } from '@antimatterhq/ui/components/skeleton';
import { TABLE_CELL_HEIGHT } from '@antimatterhq/ui/constants/table';

export type SimpleTableProps = React.HTMLAttributes<HTMLTableElement> & {
  className?: string;
  fixed?: boolean;
  variant?: 'rounded-border';
};

export type SimpleTableHeaderProps =
  React.HTMLAttributes<HTMLTableSectionElement> & {
    className?: string;
  };

export type SimpleTableBodyProps =
  React.HTMLAttributes<HTMLTableSectionElement> & {
    className?: string;
  };

export type SimpleTableFooterProps =
  React.HTMLAttributes<HTMLTableSectionElement> & {
    className?: string;
  };

export type SimpleTableRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
  className?: string;
  hover?: boolean;
  height?: number;
  border?: boolean;
  expandable?: boolean;
};

export type SimpleTableHeadProps =
  React.ThHTMLAttributes<HTMLTableCellElement> & {
    className?: string;
    bold?: boolean;
    width?: number | 'auto';
  };

export type SimpleTableCellProps =
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    className?: string;
    width?: number | 'auto';
    truncate?: boolean;
    tooltip?: boolean;
  };

export type SimpleTableCaptionProps =
  React.HTMLAttributes<HTMLTableCaptionElement> & {
    className?: string;
  };

const SimpleTable = React.forwardRef<HTMLTableElement, SimpleTableProps>(
  ({ className, fixed = false, variant, ...props }, ref) => (
    <div
      className={cn(
        'relative w-full overflow-auto',
        variant === 'rounded-border' && 'rounded-md border border-gray-300',
      )}
    >
      <table
        ref={ref}
        className={cn(
          'w-full caption-bottom text-sm',
          fixed && 'table-fixed border-collapse',
          className,
        )}
        {...props}
      />
    </div>
  ),
);

const SimpleTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  SimpleTableHeaderProps
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('border-b border-grey-300 dark:border-grey-650', className)}
    {...props}
  />
));

const SimpleTableBody = React.forwardRef<
  HTMLTableSectionElement,
  SimpleTableBodyProps
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={className} {...props} />
));

const SimpleTableFooter = React.forwardRef<
  HTMLTableSectionElement,
  SimpleTableFooterProps
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t border-grey-300 bg-grey-50 font-medium dark:border-grey-650 dark:bg-grey-800',
      className,
    )}
    {...props}
  />
));

const SimpleTableRow = React.forwardRef<
  HTMLTableRowElement,
  SimpleTableRowProps
>(
  (
    {
      className,
      hover = true,
      border = true,
      expandable = false,
      height = expandable ? undefined : TABLE_CELL_HEIGHT,
      style,
      ...props
    },
    ref,
  ) => (
    <tr
      ref={ref}
      className={cn(
        'group',
        'data-[state=selected]:bg-blue-600/8 dark:data-[state=selected]:bg-blue-400/8',
        'tbody & hover:bg-blue-600/4 dark:hover:bg-blue-400/4',
        hover && 'hover-row hover:bg-gray-100 dark:hover:bg-gray-800',
        border && 'border-b border-grey-300 dark:border-grey-650',
        height && `h-[${height}px]`,
        className,
      )}
      style={{
        ...style,
        ...(height &&
          !expandable && {
            height: `${height}px`,
          }),
        ...(expandable && {
          minHeight: height,
        }),
      }}
      {...props}
    />
  ),
);

const SimpleTableHead = React.forwardRef<
  HTMLTableCellElement,
  SimpleTableHeadProps
>(({ className, bold = true, width = 'auto', ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-8 px-2 text-left align-middle font-medium text-grey-800 dark:text-grey-100',
      '[&:has([role=checkbox])]:pr-0',
      bold && 'bold',
      width === 'auto' && 'w-auto',
      width !== 'auto' && `w-[${width}px]`,
      className,
    )}
    {...props}
  />
));

const SimpleTableCell = React.forwardRef<
  HTMLTableCellElement,
  SimpleTableCellProps
>(
  (
    {
      className,
      width = 'auto',
      truncate = true,
      tooltip = false,
      children,
      ...props
    },
    ref,
  ) => (
    <td
      ref={ref}
      className={cn(
        'px-2 py-1.5 align-middle text-grey-800 dark:text-grey-100',
        '[&:has([role=checkbox])]:pr-0',
        width === 'auto' && 'w-auto',
        width !== 'auto' && `w-[${width}px]`,
        truncate && 'truncate',
        className,
      )}
      {...props}
    >
      {!tooltip && children}
      {!!tooltip && (
        <Tooltip content={children} side="top">
          <div className={cn(truncate && 'truncate')}>{children}</div>
        </Tooltip>
      )}
    </td>
  ),
);

const SimpleTableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  SimpleTableCaptionProps
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-grey-500 dark:text-grey-350', className)}
    {...props}
  />
));

export type TableSkeletonRowsProps = {
  height?: number;
  cols: number;
  rows: number;
  'data-testid'?: string;
};

const SimpleTableSkeletonRows = ({
  height = TABLE_CELL_HEIGHT,
  cols,
  rows,
  'data-testid': dataTestId = 'simple-table-skeleton-rows',
}: TableSkeletonRowsProps) => {
  return (
    <>
      {[...Array(rows).keys()].map((index) => (
        <SimpleTableRow
          key={`skeleton-${index}`}
          border={false}
          hover={false}
          style={{ height: `${height}px` }}
          data-testid={`${dataTestId}-${index}`}
        >
          <SimpleTableCell colSpan={cols}>
            <Skeleton style={{ height: `${height - 13}px` }} />
          </SimpleTableCell>
        </SimpleTableRow>
      ))}
    </>
  );
};

SimpleTable.displayName = 'SimpleTable';
SimpleTableHeader.displayName = 'SimpleTableHeader';
SimpleTableBody.displayName = 'SimpleTableBody';
SimpleTableFooter.displayName = 'SimpleTableFooter';
SimpleTableRow.displayName = 'SimpleTableRow';
SimpleTableHead.displayName = 'SimpleTableHead';
SimpleTableCell.displayName = 'SimpleTableCell';
SimpleTableCaption.displayName = 'SimpleTableCaption';
SimpleTableSkeletonRows.displayName = 'SimpleTableSkeletonRows';

export {
  SimpleTable,
  SimpleTableHeader,
  SimpleTableBody,
  SimpleTableFooter,
  SimpleTableHead,
  SimpleTableRow,
  SimpleTableCell,
  SimpleTableCaption,
  SimpleTableSkeletonRows,
};
